import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Alert,
  Button,
  FieldShell,
  Modal,
  TextField,
  controlClass,
  mutedTextClass,
} from '@/components/layout/ui'
import { cn } from '@/lib/cn'
import { fieldErrorFor, isFormLevel, messageFor } from '@/lib/errors'
import { useWorkspace } from '@/lib/workspace'

import { normalizeProjectKeyInput, projectKeyProblem, suggestProjectKey } from './key'
import { useCreateProject, useOrgProjects } from './useProjectMutations'

/**
 * Create a project, then go straight to its board.
 *
 * The key is the only field that needs explaining: it is what every issue in
 * the project is named after (TSK-14), it cannot be changed later without
 * breaking existing links, and it has to be unique in the workspace. So it is
 * suggested from the name, shown rather than hidden, and checked against the
 * workspace's existing keys as it is typed.
 *
 * The suggestion is NOT sent unless someone edits it. Letting the server derive
 * the key is what makes two people creating a project at the same moment both
 * succeed — it steps past a collision inside the transaction, where the client
 * can only guess.
 */
export function CreateProjectModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const workspace = useWorkspace()
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [key, setKey] = useState('')
  const [keyTouched, setKeyTouched] = useState(false)
  const [description, setDescription] = useState('')

  // Archived projects keep their keys, so the uniqueness mirror needs them too.
  const existing = useOrgProjects(workspace.slug, { enabled: open, includeArchived: true })
  const takenKeys = (existing.data ?? [])
    .map((project) => project.project_key)
    .filter((value): value is string => Boolean(value))

  const create = useCreateProject(workspace.slug)

  // The mutation object is a new reference on every render; a ref keeps the
  // reset out of the effect's dependencies so opening the dialog resets it once.
  const resetMutation = useRef(create.reset)
  resetMutation.current = create.reset

  useEffect(() => {
    if (!open) return
    setName('')
    setKey('')
    setKeyTouched(false)
    setDescription('')
    resetMutation.current()
  }, [open])

  const suggestedKey = suggestProjectKey(name, takenKeys)
  const effectiveKey = keyTouched ? key : name.trim() ? suggestedKey : ''
  const localKeyProblem = keyTouched ? projectKeyProblem(effectiveKey, takenKeys) : null
  const keyError = fieldErrorFor(create.error, 'key') ?? localKeyProblem ?? undefined

  const canSubmit =
    Boolean(name.trim()) && !localKeyProblem && !create.isPending && workspace.can('content.write')

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!canSubmit) return
    create.mutate(
      {
        name: name.trim(),
        // Only a deliberately chosen key travels; otherwise the server derives
        // one and resolves collisions itself.
        key: keyTouched && effectiveKey ? effectiveKey : undefined,
        description: description.trim() || undefined,
      },
      {
        onSuccess: (project) => {
          onClose()
          navigate(workspace.path(`/projects/${project.project_key ?? project.id}/board`))
        },
      },
    )
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New project"
      description="A project holds its own board, backlog and sprints."
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {!workspace.can('content.write') ? (
          <Alert tone="warning" title="Read-only access">
            Ask a workspace admin to create the project, or to give you member access.
          </Alert>
        ) : null}

        <TextField
          label="Name"
          autoFocus
          required
          maxLength={255}
          placeholder="Mobile Apps"
          value={name}
          onChange={(event) => setName(event.target.value)}
          error={fieldErrorFor(create.error, 'name')}
        />

        <TextField
          label="Key"
          spellCheck={false}
          autoCapitalize="off"
          autoComplete="off"
          maxLength={10}
          placeholder="MA"
          className="font-mono uppercase"
          value={effectiveKey}
          onChange={(event) => {
            setKeyTouched(true)
            setKey(normalizeProjectKeyInput(event.target.value))
          }}
          hint={
            effectiveKey
              ? `Issues will be numbered ${effectiveKey}-1, ${effectiveKey}-2, and so on. It cannot be changed later.`
              : 'Suggested from the name. Issue numbers are built from it.'
          }
          error={keyError}
        />

        <FieldShell label="Description" hint="Optional. What is this project for?">
          {({ id, describedBy, invalid }) => (
            <textarea
              id={id}
              rows={3}
              maxLength={10000}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              aria-invalid={invalid || undefined}
              aria-describedby={describedBy}
              className={cn(controlClass, 'min-h-20 resize-y')}
            />
          )}
        </FieldShell>

        {create.error && isFormLevel(create.error, ['name', 'key', 'description']) ? (
          <Alert tone="danger">{messageFor(create.error)}</Alert>
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
          <p className={cn('text-xs', mutedTextClass)}>
            You can invite people to it from workspace settings.
          </p>
          <div className="flex gap-2">
            <Button onClick={onClose} disabled={create.isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={create.isPending} disabled={!canSubmit}>
              Create project
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  )
}
